// client/src/pages/AdminAuctionList.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import {
  Box, Typography, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Chip, Button, Stack, Alert, CircularProgress,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import { formatCurrency } from '../utils/currency';
import EmptyState from '../components/EmptyState';
import { API_BASE_URL } from '../utils/media';

const statusColor = { active: 'success', pending: 'warning', ended: 'default', sold: 'info' };

const AdminAuctionList = () => {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token } = useContext(AuthContext);

  const fetchAllAuctions = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/auctions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch auctions. You may not have permission.');
      setAuctions(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAllAuctions(); }, [fetchAllAuctions]);

  const handleEnd = async (id) => {
    if (!window.confirm('Force-end this auction now?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/auctions/${id}/end`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to end auction.');
      fetchAllAuctions();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Permanently remove this auction and its bid history?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/auctions/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to remove auction.');
      fetchAllAuctions();
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) return <CircularProgress size={28} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Auction Management</Typography>
      {auctions.length === 0 ? (
        <EmptyState icon={<GavelIcon />} title="No auctions yet" description="Auctions listed by sellers will appear here." />
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell><TableCell>Product Name</TableCell><TableCell>Seller</TableCell>
                <TableCell>Current Price</TableCell><TableCell>End Time</TableCell><TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {auctions.map((auction) => (
                <TableRow key={auction.id} hover>
                  <TableCell>{auction.id}</TableCell>
                  <TableCell>{auction.product_name}</TableCell>
                  <TableCell>{auction.seller_email}</TableCell>
                  <TableCell>{formatCurrency(auction.current_price)}</TableCell>
                  <TableCell>{new Date(auction.end_time).toLocaleString()}</TableCell>
                  <TableCell><Chip label={auction.status} size="small" color={statusColor[auction.status] || 'default'} /></TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {auction.status === 'active' && (
                        <Button size="small" onClick={() => handleEnd(auction.id)}>End Now</Button>
                      )}
                      <Button size="small" color="error" onClick={() => handleDelete(auction.id)}>Remove</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AdminAuctionList;
